import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Headphones, BookOpen, Video, Sparkles } from "lucide-react";

const TYPES = [
  { key: null, label: "All", icon: Sparkles },
  { key: "article", label: "Articles", icon: BookOpen },
  { key: "audio", label: "Meditations", icon: Headphones },
  { key: "video", label: "Video", icon: Video },
  { key: "affirmation", label: "Affirmations", icon: Sparkles },
];

export default function Library() {
  usePageMeta(
    "The Library — Free Articles, Meditations & Affirmations | Just be you",
    "Free personal-growth articles, guided meditations and daily affirmations by Mehjabin. Come sit — no sign-up wall."
  );
  const [filter, setFilter] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/content", { params: filter ? { type: filter } : {} }).then((r) => setItems(r.data));
  }, [filter]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative" data-testid="library-page">
      <div className="absolute top-20 right-0 w-96 h-96 bg-lavender/40 organic-blob blur-3xl -z-0 pointer-events-none" />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 relative">
        <div className="md:col-span-7">
          <div className="overline mb-5">The Living Library</div>
          <h1 className="font-serif text-5xl md:text-6xl text-lapis font-light leading-tight">
            Words, sounds,<br />and <em className="text-peach-deep">soft returns</em>.
          </h1>
        </div>
        <div className="md:col-span-5 text-ink/65 leading-relaxed">
          A growing collection — articles, meditations, podcast episodes, video teachings, and daily
          affirmations — for whenever you need to come back to yourself.
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-12 relative" data-testid="library-filters">
        {TYPES.map((t) => (
          <button key={t.label} onClick={() => setFilter(t.key)}
            data-testid={`library-filter-${(t.key || "all")}`}
            className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.15em] border transition inline-flex items-center gap-2 font-semibold ${
              filter === t.key ? "bg-lapis text-canvas border-lapis" : "border-lapis/25 text-ink/70 hover:text-lapis hover:border-lapis bg-white/60"
            }`}>
            <t.icon size={12} strokeWidth={1.6} /> {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 relative">
        {items.map((c, i) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}>
            <Link to={`/library/${c.slug}`} className="group block" data-testid={`library-card-${c.slug}`}>
              <div className="aspect-[4/3] overflow-hidden rounded-[1.75rem] relative mb-5">
                <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
                <div className="absolute top-4 left-4 overline bg-canvas/85 backdrop-blur px-3 py-1 rounded-full">{c.type} · {c.category}</div>
                {c.duration && (
                  <div className="absolute bottom-4 right-4 text-xs text-canvas bg-lapis/85 px-3 py-1 rounded-full backdrop-blur">{c.duration}</div>
                )}
              </div>
              <h3 className="font-serif text-2xl text-ink leading-tight group-hover:text-lapis transition-colors">{c.title}</h3>
              <p className="mt-2 text-sm text-ink/60 line-clamp-2">{c.excerpt}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center text-ink/50 font-serif italic text-2xl py-32" data-testid="library-empty">
          Nothing here yet — soft pieces are on their way.
        </div>
      )}
    </div>
  );
}
